import { useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, User, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { FollowButton } from '../components/FollowButton';
import { FollowerRowSkeleton } from '../components/Skeleton';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import {
  useFollowersInfinite,
  useFollowingInfinite,
  flattenFollowPages,
} from '../hooks/useUserFollowsQuery';

// Matches /users/by-username/:username FOREVER CONTRACT (flat object)
interface ProfileResponse {
  id: string;
  username: string;
  display_name?: string | null;
  avatar_url?: string | null;
  visibility?: 'public' | 'friends' | 'private';
}

export function FollowersPage() {
  const { username } = useParams<{ username: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const goBack = useBackNavigation(`/u/${username}`);

  // Determine if showing followers or following based on route
  const isFollowersTab = location.pathname.endsWith('/followers');
  const tabTitle = isFollowersTab ? 'Followers' : 'Following';

  // Compute usernameLower once for consistent cache key
  const usernameLower = (username ?? '').toLowerCase();

  // Resolve username to profile ID using React Query (cache-first)
  const profileQuery = useQuery({
    queryKey: queryKeys.users.byUsername(usernameLower),
    enabled: !!username,
    queryFn: async ({ signal }) => {
      return apiFetch<ProfileResponse>(
        `/users/by-username/${encodeURIComponent(usernameLower)}`,
        { signal }
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const profileId = profileQuery.data?.id ?? null;

  // TanStack Query for followers/following (cache-first, no reload on tab switch)
  const followersQuery = useFollowersInfinite(isFollowersTab ? profileId : null);
  const followingQuery = useFollowingInfinite(!isFollowersTab ? profileId : null);

  // Select the active query based on tab
  const activeQuery = isFollowersTab ? followersQuery : followingQuery;
  const users = useMemo(
    () => flattenFollowPages(activeQuery.data?.pages),
    [activeQuery.data?.pages]
  );

  // List area loading states (critical: don't show empty state while resolving profileId)
  const isResolvingProfileId = profileQuery.isLoading && !profileQuery.data;
  const isListInitialLoading = !!profileId && activeQuery.isLoading && !activeQuery.data;
  const showListLoading = isResolvingProfileId || isListInitialLoading;
  const showEmpty = !!profileId && !showListLoading && users.length === 0;
  // Error state
  const error =
    profileQuery.error instanceof Error
      ? profileQuery.error.message
      : activeQuery.error instanceof Error
        ? activeQuery.error.message
        : null;

  const handleLoadMore = () => {
    if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
      activeQuery.fetchNextPage();
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] px-4 sm:px-6 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={goBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">{tabTitle}</h1>
            <p className="text-sm text-slate-400">@{username}</p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 mb-6 bg-[#1A1F26]/50 rounded-lg p-1">
          <Link
            to={`/u/${username}/followers`}
            replace
            className={`flex-1 px-4 py-2 text-sm font-medium text-center rounded-md transition-colors ${
              isFollowersTab
                ? 'bg-teal-500/20 text-teal-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Followers
          </Link>
          <Link
            to={`/u/${username}/following`}
            replace
            className={`flex-1 px-4 py-2 text-sm font-medium text-center rounded-md transition-colors ${
              !isFollowersTab
                ? 'bg-teal-500/20 text-teal-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Following
          </Link>
        </div>

        {/* Error state (inline, not full-page) */}
        {error && (
          <div className="text-center py-12">
            <div className="text-xl text-slate-400 mb-4">{error}</div>
            <button
              onClick={goBack}
              className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
            >
              <ArrowLeft size={20} />
              Back
            </button>
          </div>
        )}

        {/* User list */}
        {showListLoading ? (
          <div className="space-y-2">
            {[...Array(8)].map((_, i) => (
              <FollowerRowSkeleton key={i} />
            ))}
          </div>
        ) : showEmpty ? (
          <div className="text-center py-12">
            <User size={48} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">
              {isFollowersTab ? 'No followers yet' : 'Not following anyone yet'}
            </p>
          </div>
        ) : !error && (
          <div className="space-y-2">
            {users.map((followUser) => (
              <div
                key={followUser.id}
                className="flex items-center gap-3 p-3 bg-[#1A1F26]/30 rounded-lg hover:bg-[#1A1F26]/50 transition-colors"
              >
                {/* Avatar */}
                <Link to={`/u/${followUser.username}`} className="shrink-0">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-teal-400 to-orange-500">
                    {followUser.avatar_url ? (
                      <img
                        src={followUser.avatar_url}
                        alt={followUser.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User size={24} className="text-white" />
                      </div>
                    )}
                  </div>
                </Link>

                {/* User info */}
                <div className="flex-1 min-w-0">
                  <Link to={`/u/${followUser.username}`} className="hover:underline">
                    <span className="font-medium text-white truncate block">
                      {followUser.display_name || followUser.username}
                    </span>
                    <span className="text-sm text-slate-400">@{followUser.username}</span>
                  </Link>
                </div>

                {/* Follow button - don't show for own profile */}
                {user && user.id !== followUser.id && (
                  <FollowButton userId={followUser.id} size="sm" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Load more button */}
        {activeQuery.hasNextPage && (
          <div className="mt-6 text-center">
            <button
              onClick={handleLoadMore}
              disabled={activeQuery.isFetchingNextPage}
              className="px-6 py-2 text-sm font-medium text-teal-400 bg-teal-500/10 hover:bg-teal-500/20 rounded-lg transition-colors disabled:opacity-50"
            >
              {activeQuery.isFetchingNextPage ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Loading...
                </span>
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
